import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { VRGroupService } from '../vrgroup.service';
import { BehaviorSubject, combineLatest, interval, Observable, Subject, timer } from 'rxjs';
import { find, remove, uniqBy } from 'lodash-es';
import { SidebarPanelComponent } from '../../../sidebar/sidebar-panel/sidebar-panel.component';
import { AppMessageService } from '../../../shared/app-message/app-message.service';
import { _ } from '../../../shared/utils';
import { compareAsc, differenceInMinutes, isAfter, subMinutes } from 'date-fns/esm';
import { map, mapTo, shareReplay, startWith, takeUntil, tap } from 'rxjs/operators';
import { LandingPageTile } from '../../../shared/tiles/home-tile.model';
import { PlusCode, Station } from '../common/station';
import { FilterEvent, FilterProperties, Filters } from '../../../sidebar/filters/filters.model';
import { StateService } from '../../../shared/state/state.service';
import { ScheduleTrain } from '../common/train';
import { AlarmMessage } from '../../../alarm/alarm-message';
import { AlarmService } from '../../../alarm/alarm.service';
import { ConflictAlarmTypes, TechnicalAlarmTypes } from '../../../alarm/alarm-definitions/interfaces';
import { InfoActionService } from '../../../element-utils/element-actions/info-action/info-action.service';
import { ITrainListComponent } from '../utils/train-list.type';
import { getScheduleTrainWithStatus } from '../utils/train.utils';
import { MapService } from '~app/shared/map/map.service';
import { default as getTenantConfig } from '~shared/map/layer/layer-definitions/T2';

@Component({
  selector: 'qm-vrgroup-scheduled-trains',
  templateUrl: './scheduled-trains.component.html',
  styleUrls: ['./scheduled-trains.component.scss']
})
export class ScheduledTrainsComponent extends SidebarPanelComponent implements OnInit, OnDestroy {
  public readonly columnWidths = {
    train: '4.7em',
    status: '1.2em',
    time: '6.7em',
    route: '6.5em'
  };

  public trainFilter: Map<string, FilterProperties[]> = new Map();
  public stations: string[] = [];

  public trainTypeFiltersBS = new BehaviorSubject<Filters[]>([
    {
      id: 1,
      name: _('Trains'),
      type: 'dropdown-multiple',
      noFiltersShowsAll: false,
      filter_values: [
        {
          name: _('Commuter'),
          value: 'COMMUTER',
          boolean: true
        },
        {
          name: _('Long-distance'),
          value: 'LONGDISTANCE',
          boolean: true
        },
        {
          name: _('Freight train'),
          value: 'FREIGHT',
          boolean: true
        },
        {
          name: _('Locomotive'),
          value: 'LOCOMOTIVE',
          boolean: false
        },
        {
          name: _('Other'),
          value: 'SHUNTING',
          boolean: false
        },
      ]
    }
  ]);
  public trainTypeFilters$ = this.trainTypeFiltersBS.pipe(
    map(trainTypeFilters => {
      const trainFilterMap = new Map<string, FilterProperties[]>();
      for (const s of trainTypeFilters) {
        trainFilterMap.set(s.name, s.filter_values);
      }
      return trainFilterMap;
    })
  );

  public typeAheadStations: Filters = {
    id: 1,
    name: 'Stations',
    type: 'type-ahead',
    filter_values: [],
    noFiltersShowsAll: true
  };

  public activeStationsBS = new BehaviorSubject<FilterProperties[]>([]);
  public activeStations$ = this.activeStationsBS.asObservable();

  public alarmOverlaySelectedTrain: ScheduleTrain;
  public listLimit = 40;
  public currentDate$: Observable<Date> = timer(0, 1000)
    .pipe(
      map(() => new Date()),
      shareReplay(1)
    );
  private ngUnsubscribe: Subject<boolean> = new Subject();

  public loading$: Observable<boolean> = this.vrGroupService.scheduledTrainData$
    .pipe(
      map(trains => !(trains.length > 0))
    );

  public displayedTrains$: Observable<ScheduleTrain[]> = combineLatest([
    this.vrGroupService.scheduledTrainData$,
    this.trainTypeFilters$,
    this.activeStations$
  ])
    .pipe(
      map(([scheduleTrains, trainTypeFilters, activeStations]) => {
        // Filter visible trains
        const visibleTrains = (scheduleTrains || []).filter(train => {
          for (const s of trainTypeFilters.get('Trains')) {
            if (s.value === train.type) {
              if (activeStations.length > 0 && s.boolean) {
                return !!find(activeStations, o => o.value === train.depStation);
              } else {
                return s.boolean;
              }
            }
          }
        });
        return visibleTrains
          .map(train => getScheduleTrainWithStatus(train))
          .sort((a: ScheduleTrain, b: ScheduleTrain) => {
            if (a.flagged || b.flagged) {
              return (+b.flagged) - (+a.flagged);
            } else {
              return compareAsc(a.depTime, b.depTime)
            }
          });
      })
    );

  public challengeUrl = getTenantConfig().tenantCustomData.urls.challenge;

  constructor(private vrGroupService: VRGroupService,
    private state: StateService,
    private changeDetector: ChangeDetectorRef,
    protected appMessageService: AppMessageService,
    private alarmService: AlarmService,
    private mapService: MapService,
    private infoActionService: InfoActionService) {
    super(appMessageService);
    this.componentName = ScheduledTrainsComponent.getComponentName();
  }

  public static initTile(tile: LandingPageTile) {
    tile.description = _('Scheduled train departures for next hour');
    tile.icon = 'far fa-arrow-from-left';
    tile.title = _('Train departures');
    tile.panel = ScheduledTrainsComponent.getComponentName();
  }

  private static getComponentName(): string {
    return 'ScheduledTrains';
  }

  ngOnInit() {
    console.log('Schedules loading', this.loading);
    // Get filters saved in previous session
    const [savedTrainTypeFilters, savedActiveStations] = this.state.getPartOfState('scheludedTrains_filters') || [];
    if (savedTrainTypeFilters) {
      this.trainTypeFiltersBS.next(savedTrainTypeFilters);
    }
    if (savedActiveStations) {
      this.activeStationsBS.next(savedActiveStations);
    }

    this.getStations();

    // Save active stations in state when updated
    combineLatest([
      this.trainTypeFiltersBS,
      this.activeStations$
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([trainTypeFilters, activeStations]) => {
        this.saveFilterState([
          trainTypeFilters || [],
          activeStations || []
        ]);
      });
  }

  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private stationToFilterProperties(station: Station): FilterProperties {
    return {
      name: station.stationName + ' (' + station.stationShortCode + ')',
      value: station.stationShortCode,
      boolean: this.checkIfActive({ value: station.stationShortCode })
    };
  }

  private async getStations() {
    if (this.typeAheadStations.filter_values.length < 1) {
      const stations: Station[] = await this.vrGroupService.getStationList();
      const plusCodes = await this.vrGroupService.getPlusCodes();
      this.typeAheadStations.filter_values = stations
        .map(station => this.stationToFilterProperties(station))
        .concat(
          Array.from(plusCodes.values()).map(plus => ({
            name: `${plus.name} (${plus.code})`,
            value: plus.code,
            boolean: this.checkIfActive({ value: plus.code }),
          }))
        );
    }
    this.changeDetector.markForCheck();
  }

  private checkIfActive(testedStation: FilterProperties): boolean {
    // plus-codes are never selected; they get expanded into individual stations and that would cause conflicts
    const value = '' + testedStation.value;
    if (value.endsWith('+')) {
      return false;
    }
    return !!find(this.activeStationsBS.getValue(), activeStation => activeStation.value === testedStation.value);
  }

  public removeFromActiveStations(stationToRemove: FilterProperties) {
    const prevActiveStations = this.activeStationsBS.getValue();
    const updatedStations = prevActiveStations.filter(curStation => curStation !== stationToRemove);
    this.activeStationsBS.next(updatedStations);
    this.typeAheadStations.filter_values.forEach(typeAheadStation => {
      if (stationToRemove.value === typeAheadStation.value) {
        typeAheadStation.boolean = false;
      }
    });
  }

  public async filtersChanged($event: FilterEvent): Promise<void> {
    if ($event) {
      const plusCodes = await this.vrGroupService.getPlusCodes();
      this.activeStationsBS.next(uniqBy(
        $event.typeAheadResults.filter_values
          .flatMap(props => {
            // discard non-selected options
            if (!props.boolean) {
              return [];
            }

            // expand plus codes into individual stations
            const value = '' + props.value;
            if (value.endsWith('+')) {
              return plusCodes.get(value)
                .stations
                .map(station => this.stationToFilterProperties(station));
            }

            return [props];
          }),
        props => props.name,
      ));
      this.typeAheadStations = {
        ...$event.typeAheadResults,
        filter_values: $event.typeAheadResults.filter_values.map(props => ({
          ...props,
          boolean: this.checkIfActive(props),
        }))
      };

      this.trainTypeFiltersBS.next($event.customFilters);
    }
  }

  private saveFilterState(filters: [FilterProperties[], FilterProperties[]]) {
    this.state.updateState('scheludedTrains_filters', filters);
  }

  public diffFromNow = (train: ScheduleTrain) => {
    const currentDate = new Date();
    return differenceInMinutes(subMinutes(currentDate, 90), train.depTime);
  }

  public getDependencyConflictForTrain = (train: ScheduleTrain) => {
    return this.findVrTrainConflictAlarms(train).find(alarm => {
      return alarm.metadata.active === true;
    });
  }

  public getDepartureAlarmForTrain = (train: ScheduleTrain) => {
    return train.alarms
      .filter(alarm => alarm.metadata.type === 'trainDeparture')
      .find(alarm => {
        return alarm.metadata.active === true;
      });
  }

  public selectAlarmOverlayTrain(train: ScheduleTrain) {
    this.alarmOverlaySelectedTrain = train;
  }

  public getTranslatedAlarm = (alarm: AlarmMessage) => {
    return this.alarmService.getTranslatedAlarm(alarm.metadata, false);
  }

  public findVrTrainConflictAlarms(train: ScheduleTrain) {
    return train.alarms.filter(alarm => {
      return ConflictAlarmTypes.indexOf(alarm.metadata.type) !== -1;
    });
  }

  public hasActiveTechnicalAlarms = (train: ScheduleTrain) => {
    return this.getActiveTechnicalAlarms(train).length > 0;
  }

  public getActiveTechnicalAlarms = (train: ScheduleTrain) => {
    return this.filterVrTrainTechnicalAlarms(train).filter(a => a.metadata.active);
  }

  public filterVrTrainTechnicalAlarms(train: ScheduleTrain) {
    return train.alarms.filter(alarm => {
      return TechnicalAlarmTypes.indexOf(alarm.metadata.type) !== -1;
    });
  }

  public async openTrainInfo(train: ScheduleTrain) {
    const el = await this.vrGroupService.getTrainMapElement(train.tripNumber, train.tripDate);
    this.mapService.changeCurrentFeature(el);
    this.infoActionService.openDetails(el.layer, el);
  }

  public async openCargoInfo(train: ScheduleTrain) {
    const el = await this.vrGroupService.getTrainMapElement(train.tripNumber, train.tripDate);
    this.mapService.changeCurrentFeature(el);
    this.infoActionService.openDetails(el.layer, el, 'cargomanifest');
  }

  public onLoadMoreBtnClicked() {
    this.listLimit += 30;
  }
}
